import { X } from 'lucide-react';

import { Badge } from '@workspace/ui';
import { MapFloatingSummaryCardSurface } from '@workspace/silk-ui';

import type { SalesServiceabilityZoneOverlaySelection } from '@/lib/sales-zones';

const formatZoneKind = (value: string) =>
  value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export function SalesMapServiceabilityZoneHoverCard({
  zone,
}: {
  zone: SalesServiceabilityZoneOverlaySelection;
}) {
  return (
    <MapFloatingSummaryCardSurface
      title={zone.name}
      subtitle={formatZoneKind(zone.zone_kind)}
      badges={zone.has_route_pricing_overrides ? ['Pricing override'] : []}
      summary={[
        { key: 'markets', label: 'Markets', value: String(zone.market_count) },
        { key: 'zone-key', label: 'Zone key', value: zone.zone_key },
      ]}
      className="pointer-events-none"
    />
  );
}

export function SalesMapServiceabilityZonePanel({
  zone,
  onClose,
}: {
  zone: SalesServiceabilityZoneOverlaySelection;
  onClose: () => void;
}) {
  return (
    <div className="flex h-full flex-col border-l border-border bg-background">
      <header className="shrink-0 border-b border-border bg-muted/5 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            <div className="min-w-0">
              <h2 className="truncate text-sm font-semibold text-foreground">{zone.name}</h2>
              <p className="text-xs text-muted-foreground">{formatZoneKind(zone.zone_kind)}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">
                {zone.market_count} market{zone.market_count === 1 ? '' : 's'}
              </Badge>
              {zone.has_route_pricing_overrides ? (
                <Badge variant="secondary">Pricing override</Badge>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close zone details"
            onClick={onClose}
            className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div className="rounded-lg bg-muted/40 px-3 py-3">
            <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Zone key</div>
            <div className="mt-1 break-words font-medium text-foreground">{zone.zone_key}</div>
          </div>
          <div className="rounded-lg bg-muted/40 px-3 py-3">
            <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Source</div>
            <div className="mt-1 break-words font-medium text-foreground">{zone.source_system || '—'}</div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Markets
          </div>
          {zone.market_labels.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {zone.market_labels.map(label => (
                <Badge key={label} variant="outline">
                  {label}
                </Badge>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No market labels are attached to this zone.</div>
          )}
        </div>

        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div className="rounded-lg bg-muted/40 px-3 py-3">
            <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Source ref</div>
            <div className="mt-1 break-words font-medium text-foreground">{zone.source_ref || '—'}</div>
          </div>
          <div className="rounded-lg bg-muted/40 px-3 py-3">
            <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Route pricing</div>
            <div className="mt-1 font-medium text-foreground">
              {zone.has_route_pricing_overrides ? 'Zone override present' : 'Uses default pricing'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
